<script setup lang="ts">
import { ChevronDown, Copy, Pencil, Trash2 } from 'lucide-vue-next'
import axios from 'axios'
import useLegacy from '@/views/autopilot/composables/legacy'
import useNewTemplates from '@/views/autopilot/composables/newTemplates'
import type { TabKey } from '@/components/base/BaseTab.vue'
import ActivationConfirmationModal from './Components/ActivationConfirmationModal.vue'
import DeleteConfirmationModal from './Components/DeleteConfirmationModal.vue'
import MarketingFlowPerformance from './Components/MarketingFlowPerformance.vue'
import MarketingFlowPreview from './Components/MarketingFlowPreview.vue'
import ReplaceLegacyModal from './Components/ReplaceLegacyModal.vue'

import type { Audience, LoyaltyReward } from '@/types/types'
import type { ContentScope } from '@/types/contentScope'
import { patchContentScope, useContentScopeSave } from '@/composables/contentScope'
import type { ActionSettings, DelaySettings, MarketingFlowStep, TriggerSettings } from './types/marketingFlow'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const { fetchMerchantAudiences } = useAudience()
const { fetchAllRewards } = useReward()
const { user, stores } = useLocalStorage()
const notification = useNotification()
const { send } = useEvent()

const {
  fetchMarketingFlow,
  fetchMarketingFlowPerformance,
  saveMarketingFlow,
  deleteMarketingFlow,
  isTriggerStepCompleted,
  isDelayStepCompleted,
  isActionStepCompleted,
  generateNewVersionName
} = useMarketingFlow()

const { isCustomerBirthdayUsed, isCustomerVipUsed, isCustomerAtRiskUsed } = useNewTemplates()

const { isLegacyCustomerBirthdayActive, isLegacyCustomerVipActive, isLegacyCustomerAtRiskActive } = useLegacy()

const isLoaded = ref(false)
const isStatsLoaded = ref(false)

const audiences = ref<Audience[]>(null)
const rewards = ref<LoyaltyReward[]>(null)

const marketingFlowName = ref<string>(null)

const isActive = ref<boolean>(false)
const scope = ref<ContentScope | null>(null)
const saveScope = useContentScopeSave()

const handleUpdateScope = async (value: ContentScope) => {
  scope.value = await saveScope(() => patchContentScope('marketing-flows', marketingFlowId.value, value))
}

const templateUsed = ref<string>(null)

const isActivationConfirmationModalOpen = ref(false)
const isReplaceLegacyModalOpen = ref(false)
const isDeleteConfirmationModalOpen = ref(false)
const segmentAudienceCount = ref<number | null>(null)
const isConfirming = ref(false)
const activatedAt = ref<string | null>(null)

const triggerSettings = ref<TriggerSettings>()
const delaySettings = ref<DelaySettings>()
const actionSettings = ref<ActionSettings>()

const flowPerformance = ref<{
  triggeredCustomers: number
  completedCustomers: number
  delayedCustomers: number
}>(null)

const currentTab = ref((route.query?.activeTab as string) || 'preview')
const marketingFlowId = computed(() => Number(route.params.marketingFlowId))
const tabs = ref<TabKey[]>([
  {
    value: 'preview',
    name: t('pages.marketingFlow.marketingFlowEditPage.preview')
  },
  {
    value: 'performance',
    name: t('pages.marketingFlow.marketingFlowEditPage.performance')
  }
])

const isActiveOrExecuted = computed(() => {
  return isActive.value === true || (isStatsLoaded.value && flowPerformance.value?.triggeredCustomers > 0)
})

const isActivationDisabled = computed(() => {
  return (
    !isActionStepCompleted(actionSettings.value, triggerSettings.value.selectedStores) ||
    !isDelayStepCompleted(delaySettings.value, triggerSettings.value?.triggerType) ||
    !isTriggerStepCompleted(triggerSettings.value)
  )
})

const stepHasError = computed(() => ({
  trigger: !isTriggerStepCompleted(triggerSettings.value),
  delay: !isDelayStepCompleted(delaySettings.value, triggerSettings.value?.triggerType),
  action: !isActionStepCompleted(actionSettings.value, triggerSettings.value.selectedStores)
}))

const hasRewardAction = computed(() => {
  return actionSettings.value?.actions?.some((action) => action.actionType === 'REWARD') ?? false
})

const hasEmailAction = computed(() => {
  return actionSettings.value?.actions?.some((action) => action.actionType === 'SEND_EMAIL') ?? false
})

const hasSmsAction = computed(() => {
  return actionSettings.value?.actions?.some((action) => action.actionType === 'SEND_SMS') ?? false
})

const hasPushAction = computed(() => {
  return actionSettings.value?.actions?.some((action) => action.actionType === 'SEND_PUSH') ?? false
})

const dropdownValues = computed(() => {
  return [
    {
      id: 1,
      text: t('global.words.modify'),
      icon: Pencil
    },
    {
      id: 2,
      text: t('global.words.duplicate'),
      icon: Copy
    },
    {
      id: 3,
      text: t('global.words.delete'),
      icon: Trash2,
      variant: 'destructive'
    }
  ]
})

const handleDropdownUpdate = (id: number) => {
  if (id === 1) {
    goToEdit()
  }

  if (id === 2) {
    duplicateMarketingFlow()
  }

  if (id === 3) {
    isDeleteConfirmationModalOpen.value = true
  }
}

const handleActivationConfirmationModalClose = () => {
  isActive.value = false
  isActivationConfirmationModalOpen.value = false
}

const handleDeleteConfirmationModalClose = () => {
  isDeleteConfirmationModalOpen.value = false
}

const isReplacingLegacy = computed(() => {
  if (templateUsed.value === 'CustomerBirthday') {
    return isLegacyCustomerBirthdayActive.value && isCustomerBirthdayUsed.value
  }

  if (templateUsed.value === 'CustomerVip') {
    return isLegacyCustomerVipActive.value && isCustomerVipUsed.value
  }

  if (templateUsed.value === 'CustomerAtRisk') {
    return isLegacyCustomerAtRiskActive.value && isCustomerAtRiskUsed.value
  }

  return false
})

const shouldShowAudienceOptions = computed(
  () => triggerSettings.value?.triggerType === 'SEGMENT_ENTRY' && !!triggerSettings.value.selectedAudience
)

const showAudienceChoice = computed(() => shouldShowAudienceOptions.value && !activatedAt.value)

const fetchAudienceCount = async (): Promise<number | null> => {
  if (!triggerSettings.value?.selectedAudience || !triggerSettings.value.selectedStores?.length) {
    return null
  }

  try {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/segments/statistics`, {
      params: {
        storeIds: triggerSettings.value.selectedStores,
        segmentIds: [triggerSettings.value.selectedAudience]
      }
    })

    const rows = Array.isArray(data.data) ? data.data : []
    const segmentId = Number(triggerSettings.value.selectedAudience)
    const row =
      rows.find(
        (segment: { id?: number; segment_id?: number; merchant_segment_id?: number }) =>
          Number(segment.id ?? segment.segment_id ?? segment.merchant_segment_id) === segmentId
      ) ?? (rows.length === 1 ? rows[0] : undefined)

    const count = Number(row?.customer_count)
    return Number.isFinite(count) ? count : null
  } catch (error) {
    console.error('Error fetching audience count:', error)
    return null
  }
}

const handleToggleActivation = async () => {
  if (!isActive.value) {
    deactivateMarketingFlow()
    return
  }

  if (isReplacingLegacy.value) {
    isReplaceLegacyModalOpen.value = true
    return
  }

  segmentAudienceCount.value = showAudienceChoice.value ? await fetchAudienceCount() : null
  isActivationConfirmationModalOpen.value = true
}

const goToEdit = (queryStep?: MarketingFlowStep) => {
  router.push({
    name: 'MarketingFlowEdit',
    params: { marketingFlowId: marketingFlowId.value },
    query: queryStep ? { step: queryStep } : {}
  })
}

const getSegments = async () => {
  const data = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 } })
  audiences.value = data.data.map((audience) => {
    if (audience.frequency_type) {
      audience.type = 'typology'
    }

    return {
      id: audience.id,
      name: audience.name,
      type: audience.type,
      frequency_type: audience.frequency_type
    }
  })
}

const fetchRewards = async () => {
  try {
    const data = await fetchAllRewards()
    rewards.value = data
  } catch (error) {
    console.error('Error fetching rewards:', error)
  }
}

const getMarketingFlow = async (marketingFlowId: number) => {
  const {
    marketingFlowNameValue,
    triggerSettingsValue,
    delaySettingsValue,
    actionSettingsValue,
    isActiveValue,
    activatedAtValue,
    templateUsedValue,
    scopeValue
  } = await fetchMarketingFlow({
    marketingFlowId: marketingFlowId,
    audiences: audiences.value as Audience[],
    rewards: rewards.value as LoyaltyReward[]
  })

  marketingFlowName.value = marketingFlowNameValue
  triggerSettings.value = triggerSettingsValue
  delaySettings.value = delaySettingsValue
  actionSettings.value = actionSettingsValue
  isActive.value = isActiveValue
  activatedAt.value = activatedAtValue
  scope.value = scopeValue
  templateUsed.value = templateUsedValue
}

const handleActivateMarketingFlow = async (executeOnExistingMembers?: boolean | null) => {
  if (isConfirming.value) return
  isConfirming.value = true

  try {
    if (showAudienceChoice.value) {
      await activateMarketingFlow(executeOnExistingMembers === true)
      return
    }

    await activateMarketingFlow()
  } finally {
    isConfirming.value = false
  }
}

const activateMarketingFlow = async (executeOnExistingMembers?: boolean) => {
  try {
    await saveMarketingFlow({
      marketingFlowName: marketingFlowName.value,
      merchantId: user.value.merchant.id,
      isActive: true,
      actionSettings: actionSettings.value,
      delaySettings: delaySettings.value,
      triggerSettings: triggerSettings.value,
      marketingFlowId: marketingFlowId.value,
      executeOnExistingMembers
    })

    await getMarketingFlow(marketingFlowId.value)

    isActivationConfirmationModalOpen.value = false
    isReplaceLegacyModalOpen.value = false

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioActivatedSuccessfully'),
      timeout: 3000
    })
  } catch (error) {
    if (error?.message === 'processing import or segment') {
      notification.create('error', {
        text: t('pages.marketingFlow.marketingFlowEditPage.processingSegementOrImportError'),
        timeout: 3000
      })
      isActive.value = false
      isActivationConfirmationModalOpen.value = false
    } else {
      notification.create('error', {
        text: t('pages.marketingFlow.marketingFlowEditPage.unexpectedError'),
        timeout: 3000
      })
    }
  }
}

const deactivateMarketingFlow = async () => {
  try {
    isActive.value = false

    await saveMarketingFlow({
      marketingFlowName: marketingFlowName.value,
      merchantId: user.value.merchant.id,
      isActive: false,
      actionSettings: actionSettings.value,
      delaySettings: delaySettings.value,
      triggerSettings: triggerSettings.value,
      marketingFlowId: marketingFlowId.value,
      useDeactivate: true
    })

    await getMarketingFlow(marketingFlowId.value)

    isActivationConfirmationModalOpen.value = false

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioDeactivatedSuccessfully'),
      timeout: 3000
    })
  } catch {
    notification.create('error', {
      text: t('pages.marketingFlow.marketingFlowEditPage.unexpectedError'),
      timeout: 3000
    })
  }
}

const handleReplaceLegacyModalClose = () => {
  isReplaceLegacyModalOpen.value = false
  if (route.query.fromTemplate) {
    send('segment', 'automation.template.replace_legacy.canceled', {
      template_name: route.query.fromTemplate
    })
  }
}
const handleReplaceLegacyMarketingFlow = () => {
  activateMarketingFlow()
  if (route.query.fromTemplate) {
    send('segment', 'automation.template.replace_legacy.confirmed', {
      template_name: route.query.fromTemplate
    })
  }
}

const duplicateMarketingFlow = async () => {
  try {
    const { marketingFlowIdValue } = await saveMarketingFlow({
      marketingFlowName: generateNewVersionName(marketingFlowName.value),
      merchantId: user.value.merchant.id,
      isActive: false,
      actionSettings: actionSettings.value,
      delaySettings: delaySettings.value,
      triggerSettings: triggerSettings.value
    })

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioDuplicatedSuccessfully'),
      timeout: 3000
    })

    router.push(`/marketing-flow/edit/${marketingFlowIdValue}`)
  } catch {
    notification.create('error', {
      text: t('pages.marketingFlow.marketingFlowEditPage.unexpectedError'),
      timeout: 3000
    })
  }
}

const removeMarketingFlow = async () => {
  try {
    await deleteMarketingFlow({
      marketingFlowId: marketingFlowId.value
    })

    notification.create('success', {
      text: t('pages.marketingFlow.marketingFlowEditPage.scenarioDeletedSuccessfully'),
      timeout: 3000
    })

    router.push('/autopilots')
  } catch {
    notification.create('error', {
      text: t('pages.marketingFlow.marketingFlowEditPage.unexpectedError'),
      timeout: 3000
    })
  }
}

onMounted(async () => {
  await Promise.all([getSegments(), fetchRewards()])

  if (marketingFlowId.value) {
    await getMarketingFlow(marketingFlowId.value)
    isLoaded.value = true
  } else {
    isLoaded.value = true
  }

  flowPerformance.value = await fetchMarketingFlowPerformance({
    marketingFlowId: marketingFlowId.value
  })

  isStatsLoaded.value = true
})
</script>

<template>
  <div v-if="isLoaded" class="">
    <div class="flex min-h-screen flex-col">
      <div class="bg-white">
        <TheDetailHeader
          :title="marketingFlowName"
          :parent="{ label: t('pages.autopilot.autopilotsPage.title'), to: { name: 'AutopilotList' } }"
        >
          <template #badge>
            <BaseChip
              :color="isActive ? 'green' : 'yellow'"
              :text="
                isActive
                  ? t('pages.autopilot.autopilotsPage.items.active')
                  : t('pages.autopilot.autopilotsPage.items.inactive')
              "
            />
            <ContentScopeDetailBadge :scope subject="automation" :save="handleUpdateScope" />
          </template>
          <template #actions>
            <Tooltip :open="isActivationDisabled ? undefined : false">
              <TooltipTrigger as-child>
                <span>
                  <BaseSwitch
                    :id="'is-active'"
                    v-model="isActive"
                    class="mx-4"
                    :is-disabled="isActivationDisabled"
                    @click="handleToggleActivation"
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{{
                t('pages.marketingFlow.marketingFlowEditPage.activationDisabledTooltip')
              }}</TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="outline">
                  {{ t('pages.marketingFlow.marketingFlowEditPage.manageScenario') }}
                  <ChevronDown class="ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  v-for="action in dropdownValues"
                  :key="action.id"
                  :variant="action.variant"
                  @click="handleDropdownUpdate(action.id)"
                >
                  <component :is="action.icon" />
                  {{ action.text }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </template>
        </TheDetailHeader>
      </div>

      <div class="bg-white">
        <BaseTab v-model="currentTab" :tabs="tabs" is-full-width />
      </div>

      <div
        v-if="currentTab === 'preview'"
        class="dotted-background flex flex-1 flex-col items-center bg-content-bg px-6 pb-6 pt-16 text-foreground"
      >
        <MarketingFlowPreview
          :trigger-settings="triggerSettings"
          :delay-settings="delaySettings"
          :action-settings="actionSettings"
          :audiences="audiences"
          :rewards="rewards"
          :stores="stores"
          :disabled-card-click="{
            trigger: isActiveOrExecuted,
            delay: isActiveOrExecuted,
            action: isActiveOrExecuted
          }"
          :step-has-error="stepHasError"
          show-stats
          :flow-performance="flowPerformance"
          :is-stats-loaded="isStatsLoaded"
          @trigger-card-clicked="goToEdit('trigger')"
          @delay-card-clicked="goToEdit('delay')"
          @action-card-clicked="goToEdit('action')"
        />
      </div>

      <div v-if="currentTab === 'performance'" class="bg-content-bg p-6 md:px-12 md:py-8">
        <MarketingFlowPerformance
          :marketing-flow-id="marketingFlowId"
          :has-reward-action="hasRewardAction"
          :has-email-action="hasEmailAction"
          :has-sms-action="hasSmsAction"
          :has-push-action="hasPushAction"
        />
      </div>
    </div>

    <ActivationConfirmationModal
      :show-save-and-quit-button="false"
      :is-open="isActivationConfirmationModalOpen"
      :handle-close="handleActivationConfirmationModalClose"
      :trigger-settings="showAudienceChoice ? triggerSettings : undefined"
      :audiences="audiences ?? undefined"
      :audience-count="segmentAudienceCount"
      :is-processing="isConfirming"
      @on-activate="handleActivateMarketingFlow"
      @on-cancel="handleActivationConfirmationModalClose"
    />

    <ReplaceLegacyModal
      :is-open="isReplaceLegacyModalOpen"
      :handle-close="handleReplaceLegacyModalClose"
      @on-confirm="handleReplaceLegacyMarketingFlow"
      @on-cancel="handleReplaceLegacyModalClose"
    />

    <DeleteConfirmationModal
      :is-open="isDeleteConfirmationModalOpen"
      :handle-close="handleDeleteConfirmationModalClose"
      @on-confirm="removeMarketingFlow"
    />
  </div>
  <TheLoadingPage v-else />
</template>
